import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { IdbStorageService, IDB_STORE_CACHE } from './idb-storage.service';
import { environment } from '../../../environments/environment';
import { Job, MAEWStatus, TimeSlot } from '../models/job.model';
import { isFullSyncResponse, jobListToModel } from '../api/adapters';
import { storagePrefix } from '../utils/storage-key';
import { AuthService } from './auth.service';
import { ReferenceDataService } from './reference-data.service';
import { UpqueueService } from './upqueue.service';

/**
 * Manages the list of open jobs assigned to the authenticated process server.
 *
 * @remarks
 * Automatically fetches jobs from `GET /process-server/{serverId}/jobs`
 * whenever the user becomes authenticated, and clears them on logout.
 * The `serverId` is derived from the JWT claim (`token.user.entityId`).
 *
 * All state is held in Angular Signals so components re-render reactively
 * without manual subscriptions.
 *
 * Phase 12 note: `loadJobs()` is already wired to the real API. Computed
 * filter signals (`lateJobs`, `noAttemptJobs`, etc.) derive from `openJobs`
 * automatically.
 *
 * @example
 * ```ts
 * const jobs = inject(JobsService);
 * const late = jobs.lateJobs(); // reactive computed
 * await jobs.loadJobs();        // explicit refresh
 * ```
 */
@Injectable({ providedIn: 'root' })
export class JobsService {
  private readonly http            = inject(HttpClient);
  private readonly authService     = inject(AuthService);
  private readonly refDataService  = inject(ReferenceDataService);
  private readonly upqueueService  = inject(UpqueueService);
  private readonly idb             = inject(IdbStorageService);

  /**
   * Process server entity ID of the currently logged-in user.
   * Captured when authenticated so the correct scoped storage key can be used
   * during cleanup (when `token()` is already null on logout).
   */
  private currentServerId: number | null = null;

  /** All open jobs currently assigned to this process server. */
  readonly openJobs = signal<Job[]>([]);

  /** `true` while an API fetch is in progress. */
  readonly loading = signal(false);

  /** Unix ms timestamp of the last successful `loadJobs()` fetch, or `null` before first fetch. */
  readonly cacheAge = signal<number | null>(null);

  // ─── Computed filter views ────────────────────────────────────────────────
  // All conditions mirror the legacy job-list filter methods (isOpenJob, lateJob,
  // unAcceptedJob, noAttempt, morningJob …) ported to the new API field mapping.
  // `jobStatus !== 1` = not closed; `completionStatus === ''` = not yet served.
  //
  // Every list below derives from `listableJobs`, NOT `openJobs` directly, so a
  // job whose completion is still sitting unsent in the upqueue is hidden from
  // all of them — see `pendingCompletionJobIds`.

  /**
   * Job IDs whose terminal completion is still sitting unsent in the upqueue.
   *
   * @remarks
   * A submitted completion is hidden locally by `updateCompletionStatus(id,
   * 'Pending')`, but `mergeJobs()` preserves only `maew` across a sync — the
   * API's `completion_status` overwrites the local one on the very next fetch
   * (≤60s). If the backend hasn't recorded the completion yet (still queued,
   * in flight, or silently dropped), the job reappears. The durable fact is
   * the queue entry itself, so that is what the lists key off.
   *
   * Predicate notes:
   * - **`'attempt'` submissions only.** `'affidavit'` payloads carry no
   *   completion type, and `'accept'` payloads set `jobId` to a *comma-joined*
   *   list of job numbers, so it would never match a single `Job.id` anyway.
   *   Neither is a completion.
   * - **Truthy `completion_type_id`, not merely present.** `mapAttemptRecord()`
   *   always writes the key, and `resolveCompletionType()` yields `''` for a
   *   routine non-terminal attempt — which must stay visible. Truthy values are
   *   `'1'`/`'2'` (Served), `'3'` (Multi Attempt), `'4'` (Bad Address) and
   *   `'6'` (Posting). A truthy test also tolerates the `AttemptAdapter` path,
   *   which omits the key entirely when empty.
   */
  readonly pendingCompletionJobIds = computed(() => new Set(
    this.upqueueService.items()
      .filter(s => s.type === 'attempt' && !!s.payload['completion_type_id'])
      .map(s => s.jobId),
  ));

  /**
   * `openJobs` minus any job awaiting a queued completion — the base for every
   * derived list view.
   *
   * @remarks
   * This is a **display** filter only. The job stays in `openJobs` so it keeps
   * syncing and persisting, and `getJob()`/`findByJobNum()` deliberately bypass
   * it — `AttemptLogComponent` and `VerifyComponent` resolve their job through
   * `getJob()` and both redirect to `/jobs` with a "has been unassigned" toast
   * when it returns `undefined`, which must not fire on the officer's own
   * submission.
   */
  private readonly listableJobs = computed(() => {
    const hidden = this.pendingCompletionJobIds();
    return hidden.size === 0 ? this.openJobs() : this.openJobs().filter(j => !hidden.has(j.id));
  });

  /**
   * Jobs that are still active and accepted by the process server.
   *
   * @remarks
   * Mirrors legacy `isOpenJob()`:
   * `job_status != 1 && job_accepted == 1 && job_onhold == 0 && completion_status == ''`.
   *
   * Used as the "Open jobs" count on the dashboard and as the denominator
   * for MAEW slot breakdowns. Excludes closed, unaccepted, on-hold, and
   * completed jobs — matching exactly what the legacy app counted as "open".
   */
  readonly activeJobs = computed(() =>
    this.listableJobs().filter(
      (j) => j.jobStatus !== 1 && j.accepted && !j.onHold && j.completionStatus === '',
    ),
  );

  /**
   * Jobs that are overdue (due date before today) and not yet completed.
   * Mirrors legacy `lateJob()`: `job_status != 1 && due_date < today && due_date != '0000-00-00' && completion_status == ''`.
   */
  readonly lateJobs = computed(() =>
    this.listableJobs().filter(
      (j) => j.jobStatus !== 1 && j.late && j.completionStatus === '',
    ),
  );

  /**
   * Jobs the process server has not yet accepted.
   * Mirrors legacy `unAcceptedJob()`: `job_status != 1 && job_accepted == 0 && job_onhold == 0 && completion_status == ''`.
   */
  readonly unacceptedJobs = computed(() =>
    this.listableJobs().filter(
      (j) => j.jobStatus !== 1 && !j.accepted && !j.onHold && j.completionStatus === '',
    ),
  );

  /**
   * Accepted jobs with no attempts recorded by the API, assigned >72 hours ago.
   *
   * @remarks
   * Condition: `job_status != 1 && job_accepted == 1 && completion_status == ''
   *             && attempts.length == 0 && date_assigned > 72h`.
   * `onHold` and `late` are not excluded — a late or on-hold job with no attempts
   * still appears here.
   * `attemptsMade` (from `attempts.length` in the API response) is the authoritative source;
   * MAEW flags are local-only and always start `false` on load.
   * The 72-hour gate mirrors the legacy `checkTimeDiff()` threshold.
   */
  readonly noAttemptJobs = computed(() =>
    this.listableJobs().filter(
      (j) =>
        j.jobStatus !== 1 &&
        j.accepted &&
        j.completionStatus === '' &&
        j.attemptsMade === 0 &&
        this.isAssignedOver72h(j.dateAssigned),
    ),
  );

  /**
   * Jobs served and awaiting e-signature (drives the Sign tab badge).
   * `esignStatus === 'N'` only — new/unsigned. `esignStatus === 'R'` (sent for review)
   * is handled separately by `reviewSignJobs` and shown in the TL-review section.
   */
  /**
   * Jobs ready to sign: served, esign not yet started (`N`), awaiting signature.
   * Drives the Sign tab badge and "Ready to sign" section.
   */
  readonly signJobs = computed(() =>
    this.listableJobs().filter(
      (j) =>
        j.esignStatus === 'N' &&
        j.approvedStatus === 0 && j.manner > 0 && j.jobStatus === 1,
    ),
  );

  /**
   * Jobs sent for TL review (`R`): signed by the server but awaiting
   * Titanium Legal approval — shown in "Waiting for TL review" section.
   */
  readonly reviewSignJobs = computed(() =>
    this.listableJobs().filter(
      (j) =>
        j.esignStatus === 'R' &&
        j.approvedStatus === 0 && j.manner > 0 && j.jobStatus === 1,
    ),
  );

  /**
   * Jobs signed and awaiting TL approval — shown in "Signed — awaiting TL approval" section.
   * Covers `esignStatus: 'A'` only. Excludes `'N'` (not yet signed) and `'R'`
   * (rejected by PS — those appear in {@link reviewSignJobs} / "Waiting for TL review").
   */
  readonly signedAwaitingJobs = computed(() =>
    this.listableJobs().filter(
      (j) =>
        j.esignStatus !== 'N' && j.esignStatus !== 'R' &&
        j.approvedStatus === 0 && j.manner > 0 && j.jobStatus === 1,
    ),
  );

  /**
   * Jobs visible in the global search overlay — the union of the five actionable
   * categories: open, late, unaccepted, no-attempt, and sign-queue.
   *
   * @remarks
   * Condition breakdown:
   * - `jobStatus !== 1 && completionStatus === '' && (!onHold || late)`:
   *   covers active (open), late, and unaccepted jobs. On-hold jobs are
   *   included only if they are also late (they appear in `lateJobs`).
   * - `signJobs` condition: served jobs awaiting e-signature.
   * No other jobs (completed, on-hold non-late, etc.) appear in search.
   */
  readonly searchableJobs = computed(() =>
    this.listableJobs().filter(
      (j) =>
        (j.jobStatus !== 1 && j.completionStatus === '' && (!j.onHold || j.late)) ||
        ((j.esignStatus === 'N' || j.esignStatus === 'R') &&
          j.approvedStatus === 0 && j.manner > 0 && j.jobStatus === 1),
    ),
  );

  /**
   * Fully approved and completed jobs.
   * Mirrors legacy `jobCompleted()`:
   * `job_status == 1 && job_esign_status == 'A' && job_approved_status == 1 && completion_status == 'Approved'`.
   */
  readonly completedJobs = computed(() =>
    this.listableJobs().filter(
      (j) =>
        j.jobStatus === 1 && j.esignStatus === 'A' &&
        j.approvedStatus === 1 && j.completionStatus === 'Approved',
    ),
  );

  /**
   * Jobs that should appear in the Print Queue and drive the Print tab badge.
   *
   * @remarks
   * Union of three categories (deduped by ID):
   * - **Open** — accepted, active, non-on-hold jobs (`activeJobs`)
   * - **Late** — overdue jobs (`lateJobs`)
   * - **No attempt yet** — assigned 72h+ ago with 0 attempts (`noAttemptJobs`)
   *
   * IDs are collected into a `Set`, then `openJobs` is filtered once to
   * preserve its original sort order.
   */
  readonly printQueueJobs = computed(() => {
    const ids = new Set([
      ...this.activeJobs().map(j => j.id),
      ...this.lateJobs().map(j => j.id),
      ...this.noAttemptJobs().map(j => j.id),
    ]);
    return this.openJobs().filter(j => ids.has(j.id));
  });

  /**
   * Active open jobs not yet marked as printed.
   * Drives the Print tab badge — count decreases as jobs are marked printed.
   * Matches `activeJobs` so badge count equals what is shown in the print queue.
   */
  readonly unprintedJobs = computed(() =>
    this.activeJobs().filter((j) => !j.printed),
  );

  /**
   * Jobs marked as printed locally.
   * Used by `PrintQueueComponent` to render the "✓ Printed" tag and
   * prevent re-selection. `printed` is never set by the API.
   */
  readonly printedJobs = computed(() =>
    this.listableJobs().filter((j) => j.printed),
  );

  /**
   * Accepted jobs available for a morning service attempt that have not yet had
   * a morning attempt logged.
   *
   * @remarks
   * A job appears here when Morning has not yet been attempted (`!maew.M`).
   */
  readonly morningJobs = computed(() =>
    this.listableJobs().filter(
      (j) => j.jobStatus !== 1 && j.accepted && j.completionStatus === '' && !j.maew.M,
    ),
  );

  /**
   * Accepted jobs available for an afternoon service attempt that have not yet had
   * an afternoon attempt logged.
   *
   * @remarks
   * A job appears here when Afternoon has not yet been attempted (`!maew.A`).
   */
  readonly afternoonJobs = computed(() =>
    this.listableJobs().filter(
      (j) => j.jobStatus !== 1 && j.accepted && j.completionStatus === '' && !j.maew.A,
    ),
  );

  /**
   * Accepted jobs available for an evening service attempt that have not yet had
   * an evening attempt logged.
   *
   * @remarks
   * A job appears here when Evening has not yet been attempted (`!maew.E`).
   */
  readonly eveningJobs = computed(() =>
    this.listableJobs().filter(
      (j) => j.jobStatus !== 1 && j.accepted && j.completionStatus === '' && !j.maew.E,
    ),
  );

  /**
   * Accepted jobs available for a weekend service attempt that have not yet had
   * a weekend attempt logged.
   *
   * @remarks
   * A job appears here when Weekend has not yet been attempted (`!maew.W`).
   */
  readonly weekendJobs = computed(() =>
    this.listableJobs().filter(
      (j) => j.jobStatus !== 1 && j.accepted && j.completionStatus === '' && !j.maew.W,
    ),
  );

  constructor() {
    // Capture the server ID on login so the logout cleanup can reference it
    // after the token is already cleared. Sync orchestration is handled by
    // SyncService — this effect only manages local state lifecycle.
    effect(() => {
      if (this.authService.isAuthenticated()) {
        this.currentServerId = this.authService.token()?.user?.entityId ?? null;
      } else {
        const prevId = this.currentServerId;
        this.currentServerId = null;
        this.openJobs.set([]);
        void this.clearCache(prevId);
        if (prevId) this.refDataService.clearCache(prevId);
      }
    });
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  /**
   * Fetches the open-job list from the API and updates {@link openJobs}.
   *
   * @remarks
   * Called by {@link SyncService} as the final step of every sync cycle.
   * Returns immediately when no token is present.
   * Sets {@link loading} to `true` for the duration of the request.
   *
   * @param lastSyncTimestamp - UTC Unix timestamp (seconds) of the previous sync.
   *   When provided, an incremental diff is *requested*; omit for a full fetch.
   *   Whether the response is actually merged incrementally still depends on
   *   the response's own `full_sync` field — see `isFullSyncResponse()`.
   * @returns `true` when the fetch completed successfully (including the
   *   "incremental response was empty, nothing changed" case); `false` when
   *   there was no auth token or the request failed. Callers that only need
   *   background-refresh resilience can ignore the return value — errors are
   *   still swallowed internally and `openJobs` is left at its last value.
   * @throws Never — errors are swallowed; `openJobs` remains at its last value.
   */
  async loadJobs(lastSyncTimestamp?: number): Promise<boolean> {
    const token = this.authService.token();
    if (!token) return false;

    const serverId = token.user.entityId;
    const base     = `${environment.apiUrl}/process-server/${serverId}/jobs`;
    const url      = lastSyncTimestamp != null
      ? `${base}?lastsynctimestamp=${lastSyncTimestamp}`
      : base;

    this.loading.set(true);
    try {
      const raw      = await firstValueFrom(this.http.get<unknown>(url));
      const incoming = jobListToModel(raw);
      const now      = Date.now();
      const pfx      = storagePrefix(serverId);

      // The server's own `full_sync` declaration takes priority over whether
      // *we* requested an incremental diff — see `isFullSyncResponse()`'s doc
      // comment for why (a backend can fall back to a full list even when a
      // timestamp was sent, and merging that as if it were incremental would
      // never remove jobs no longer present in it).
      const merged = (lastSyncTimestamp != null && !isFullSyncResponse(raw))
        ? this.mergeJobs(this.openJobs(), incoming)
        : incoming;

      if (merged === this.openJobs()) return true; // incremental with empty response — no change

      this.openJobs.set(merged);
      await this.writeCache(pfx, merged, now);
      this.cacheAge.set(now);
      return true;
    } catch {
      // Leave openJobs as-is; auth interceptor already shows a toast on failure.
      return false;
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Merges an incremental API response into the current job list.
   *
   * @remarks
   * - Existing jobs whose ID appears in `incoming` are replaced, but attempt
   *   flags (M/A/E/W) are merged via OR so any offline attempt recorded before
   *   the server confirms it is not lost.
   * - Jobs present in `incoming` but absent from `current` are appended.
   * - Returns the same `current` reference when `incoming` is empty so the
   *   caller can skip the storage write.
   *
   * @param current  - Snapshot of `openJobs` before this sync cycle.
   * @param incoming - Jobs returned by the incremental API response.
   */
  private mergeJobs(current: Job[], incoming: Job[]): Job[] {
    if (incoming.length === 0) return current;

    const map = new Map(current.map(j => [j.id, j]));

    for (const job of incoming) {
      const existing = map.get(job.id);
      if (existing) {
        map.set(job.id, {
          ...job,
          maew: {
            M: existing.maew.M || job.maew.M,
            A: existing.maew.A || job.maew.A,
            E: existing.maew.E || job.maew.E,
            W: existing.maew.W || job.maew.W,
          },
        });
      } else {
        map.set(job.id, job);
      }
    }

    return [...map.values()];
  }

  /**
   * Returns the job with the given ID, or `undefined` if not found.
   *
   * @param id - Titanium Legal job ID (e.g. `"337662"`).
   */
  getJob(id: string): Job | undefined {
    return this.openJobs().find((j) => j.id === id);
  }

  /**
   * Returns the job whose printed work-order number matches `jobNum`,
   * or `undefined` if not found in the current open-jobs list.
   *
   * @remarks
   * QR codes on work orders encode the sequential job number (`jobNum`),
   * which may differ from the Titanium Legal internal `id` once the API
   * adopts a `TL-xxxx` ID format. Use this for scan-to-navigate lookups;
   * fall back to {@link getJob} for legacy barcodes that encode the `id`.
   *
   * @param jobNum - The sequential job number encoded in QR/barcodes (e.g. `"337662"`).
   */
  findByJobNum(jobNum: string): Job | undefined {
    return this.openJobs().find((j) => j.jobNum === jobNum);
  }

  /**
   * Records that a service attempt was made in the given time slot.
   *
   * @remarks
   * Mutates the in-memory signal AND persists the change to SecureStorage via
   * {@link persistCache} so the updated MAEW survives an app restart before the
   * upqueue has been flushed.  Without persistence, an offline Attempt submit
   * followed by an app restart would revert MAEW to its pre-submit values.
   *
   * @param jobId - ID of the job that was attempted.
   * @param slot  - Display time-slot label from `Job.slot` (e.g. `'Morning'`).
   */
  recordAttempt(jobId: string, slot: TimeSlot): void {
    const maewKey: Record<TimeSlot, keyof MAEWStatus> = {
      Morning:   'M',
      Afternoon: 'A',
      Evening:   'E',
      Weekend:   'W',
    };
    const key = maewKey[slot];
    this.openJobs.update((jobs) =>
      jobs.map((j) =>
        j.id === jobId ? { ...j, maew: { ...j.maew, [key]: true } } : j,
      ),
    );
    void this.persistCache();
  }

  /**
   * POSTs job numbers to the accept-queue endpoint and marks them as
   * accepted in the local signal on success.
   *
   * @param jobNums - Integer job numbers to accept.
   * @returns `true` when the server confirmed acceptance; `false` on any
   *   non-success status or HTTP error (auth interceptor shows the toast).
   */
  /**
   * Accepts the specified jobs using a queue-first strategy.
   *
   * @remarks
   * Always enqueues a `type: 'accept'` submission and optimistically marks
   * all jobs accepted in the local signal before any network attempt.
   * The HTTP POST to `/jobs/acceptqueue` is handled by
   * {@link UpqueueAdapter.transmitAll}, called by
   * {@link AcceptService.confirmAccept} when the device is online.
   *
   * `JobsService` cannot call `transmitAll()` directly —
   * `UpqueueAdapter → SignService → JobsService` is a circular dependency.
   *
   * @param jobNums - Numeric job numbers to accept.
   * @returns `'queued'` when the submission was enqueued successfully,
   *   `'failed'` when `jobNums` is empty or no auth token is available.
   */
  async acceptJobs(jobNums: number[]): Promise<'success' | 'queued' | 'failed'> {
    const token = this.authService.token();
    if (!token || jobNums.length === 0) return 'failed';

    this.upqueueService.enqueue({
      jobId:     jobNums.join(','),
      type:      'accept',
      payload:   { job_numbers: jobNums },
      timestamp: new Date().toISOString(),
      upqueued:  false,
    });
    this.markJobsAccepted(jobNums);
    return 'queued';
  }

  /**
   * Marks the given job numbers as accepted in the local `openJobs` signal
   * and persists the updated list to SecureStorage.
   *
   * @param jobNums - Numeric job numbers that were accepted.
   */
  private markJobsAccepted(jobNums: number[]): void {
    const accepted = new Set(jobNums.map(String));
    this.openJobs.update((jobs) =>
      jobs.map((j) => accepted.has(j.id) ? { ...j, accepted: true } : j),
    );
    void this.persistCache();
  }

  /**
   * Marks the job as printed in the local in-memory signal and persists the
   * change to SecureStorage.
   *
   * @remarks
   * The v2 API seeds `printed`/`printCount` from `job_printed` on load;
   * calling this method flips the flag and bumps the count optimistically
   * for jobs printed in the current session, ahead of the next `loadJobs()`
   * refresh.
   *
   * **Corrected 2026-07-29** — this method previously did NOT call
   * `persistCache()` (unlike every other mutator here, e.g. `recordAttempt()`/
   * `markJobsAccepted()`), on the stated assumption that "the v2 API is the
   * authoritative source on next load" made persisting the bump pointless.
   * That assumption doesn't hold in practice: real live `/jobs` responses
   * confirm `job_printed` stays `0` even after logged completions — this
   * app's print action doesn't reliably push a count back to the backend at
   * all (see `PrintService.printedSet`'s doc comment) — so there's no
   * guarantee a "next load" ever corrects it. Not persisting this bump meant
   * `{hash}_cache_jobs`'s stored `printCount` for a printed job silently
   * never updated (confirmed live by inspecting the actual SecureStorage
   * value), so a reload that restores from this cache without a subsequent
   * successful full sync (e.g. offline right after printing) would show the
   * job as unprinted again. Now calls `persistCache()` like every other
   * mutator, for consistency and correctness. This `printCount` bump —
   * persisted here so it survives the gap between `restoreCache()` and the
   * following `fullSync()` on a reload — is also what
   * `PrintQueueComponent.effectiveCount()` displays directly as the reprint
   * count; once that (or any later) sync actually responds, the server's
   * value is authoritative and replaces this local one, even downward
   * (explicit product decision — the API must always win once it
   * responds). `PrintService.printedSet` is a separate, membership-only
   * concern (`hasEverBeenPrinted()`/`printBadge`), not the count.
   *
   * @param jobId - Titanium Legal job ID (e.g. `"337662"`).
   */
  markPrinted(jobId: string): void {
    this.openJobs.update((jobs) =>
      jobs.map((j) => j.id === jobId
        ? { ...j, printed: true, printCount: j.printCount + 1 }
        : j),
    );
    void this.persistCache();
  }

  /**
   * Returns `true` when all four MAEW slots have been attempted.
   *
   * @param maew - The job's `maew` attempt flags.
   */
  isExhausted(maew: MAEWStatus): boolean {
    return maew.M && maew.A && maew.E && maew.W;
  }

  /**
   * Updates MAEW flags from an authoritative API response.
   * Called by `AttemptAdapter` after a successful `POST /attempts`.
   *
   * @param jobId - Titanium Legal job ID.
   * @param maew  - Partial MAEW update from the server (merged into existing).
   */
  updateMaew(jobId: string, maew: Partial<MAEWStatus>): void {
    this.openJobs.update((jobs) =>
      jobs.map((j) => j.id === jobId ? { ...j, maew: { ...j.maew, ...maew } } : j),
    );
  }

  /**
   * Marks all four MAEW slots as attempted when the API confirms `all_maew_done`.
   * Ensures `isExhausted()` returns `true` on the confirmation screen.
   *
   * @param jobId - Titanium Legal job ID.
   */
  markExhausted(jobId: string): void {
    this.openJobs.update((jobs) =>
      jobs.map((j) =>
        j.id === jobId ? { ...j, maew: { M: true, A: true, E: true, W: true } } : j,
      ),
    );
  }

  /**
   * Updates the `esignStatus` of a job in the in-memory signal and persists
   * the updated cache to SecureStorage so the status survives app restart.
   *
   * @remarks
   * Called after signing an affidavit (both online and offline paths) so
   * the job leaves `signJobs` (which filters `esignStatus === 'N'`) immediately.
   * Setting `esignStatus` to `'R'` moves the job into `signedAwaitingJobs`
   * (pending TL review), consistent with what the API sets on confirmation.
   *
   * @param jobId      - Titanium Legal job ID.
   * @param esignStatus - New e-sign status string (e.g. `'R'` for pending review).
   */
  updateJobEsignStatus(jobId: string, esignStatus: string): void {
    this.openJobs.update((jobs) =>
      jobs.map((j) => j.id === jobId ? { ...j, esignStatus } : j),
    );
    void this.persistCache();
  }

  /**
   * Updates a job's `completionStatus` in the in-memory signal and persists
   * the change to SecureStorage so it survives an app restart.
   *
   * @remarks
   * Called after a Served or Bad Address attempt is queued so the job exits
   * all "open" computed views (`activeJobs`, `lateJobs`, `noAttemptJobs`, etc.)
   * immediately — before the upload queue is flushed and the API confirms.
   * Setting `'Pending'` mirrors the server-side state while the submission
   * is in flight.
   *
   * @param jobId  - Titanium Legal job ID.
   * @param status - New completion status string (e.g. `'Pending'`).
   */
  updateCompletionStatus(jobId: string, status: string): void {
    this.openJobs.update((jobs) =>
      jobs.map((j) => j.id === jobId ? { ...j, completionStatus: status } : j),
    );
    void this.persistCache();
  }

  // ─── Private helpers ─────────────────────────────────────────────────────────

  /**
   * Returns `true` when the job was assigned more than 72 hours ago.
   *
   * @remarks
   * Jobs assigned within 72 hours are excluded from `noAttemptJobs` because it
   * is normal to have no attempts on a recently assigned job — only sustained
   * inaction beyond 72 hours warrants the "No Attempt" alert.
   *
   * @param dateAssigned - ISO date string from the API (`date_assigned`), or empty string.
   */
  private isAssignedOver72h(dateAssigned: string): boolean {
    if (!dateAssigned) return false;
    const SEVENTY_TWO_HOURS_MS = 72 * 60 * 60 * 1000;
    return Date.now() - new Date(dateAssigned).getTime() > SEVENTY_TWO_HOURS_MS;
  }

  /**
   * Persists a job snapshot plus its fetch timestamp.
   *
   * @remarks
   * Both keys live in the IndexedDB `cache` store (2026-08-05 — see
   * {@link IdbStorageService} for why they left `capacitor-secure-storage-plugin`),
   * each with a legacy SecureStorage fallback for environments where IndexedDB
   * is unusable. Failures are swallowed: the job list is re-fetchable, so a
   * failed write costs a cold start rather than data.
   *
   * @param pfx - `storagePrefix(entityId)` for the current user.
   * @param jobs - The snapshot to persist.
   * @param now - UTC Unix seconds of the fetch that produced it.
   */
  private async writeCache(pfx: string, jobs: Job[], now: number): Promise<void> {
    await this.idb.setOrFallback(IDB_STORE_CACHE, `${pfx}_cache_jobs`, jobs, () => JSON.stringify(jobs));
    await this.idb.setOrFallback(IDB_STORE_CACHE, `${pfx}_cache_jobs_at`, now, () => String(now));
  }

  /**
   * Writes the current `openJobs` snapshot to the IndexedDB cache store.
   *
   * @remarks
   * Called after any in-place mutation (e.g. `updateJobEsignStatus`) so
   * the change survives an app restart without waiting for the next
   * full `loadJobs()` network fetch.  No-ops silently when no server ID
   * is available (e.g. during logout).
   */
  private async persistCache(): Promise<void> {
    if (!this.currentServerId) return;
    const pfx = storagePrefix(this.currentServerId);
    await this.idb.setOrFallback(
      IDB_STORE_CACHE,
      `${pfx}_cache_jobs`,
      this.openJobs(),
      () => JSON.stringify(this.openJobs()),
    );
  }

  /**
   * Populates `openJobs` from the persisted cache on startup.
   *
   * @remarks
   * Called before `loadJobs()` on every login so stale data is visible
   * immediately while the network fetch is in-flight. Resolves silently
   * when no cache exists (first run or post-logout).
   */
  async restoreCache(): Promise<void> {
    if (!this.currentServerId) return;
    const pfx = storagePrefix(this.currentServerId);
    const jobs = await this.idb.getOrMigrate<Job[]>(
      IDB_STORE_CACHE,
      `${pfx}_cache_jobs`,
      (raw) => JSON.parse(raw) as Job[],
    );
    if (jobs) this.openJobs.set(jobs);

    const at = await this.idb.getOrMigrate<number>(
      IDB_STORE_CACHE,
      `${pfx}_cache_jobs_at`,
      (raw) => Number(raw),
    );
    if (at !== null) this.cacheAge.set(at);
  }

  /**
   * Removes the persisted job cache for the given server ID on logout.
   *
   * @remarks
   * Accepts `serverId` as a parameter because `token()` is already null
   * by the time this runs (the effect captures the value before clearing).
   *
   * @param serverId - The entity ID of the user who just logged out.
   */
  private async clearCache(serverId: number | null): Promise<void> {
    if (!serverId) return;
    const pfx = storagePrefix(serverId);
    await this.idb.removeEverywhere(IDB_STORE_CACHE, `${pfx}_cache_jobs`);
    await this.idb.removeEverywhere(IDB_STORE_CACHE, `${pfx}_cache_jobs_at`);
    this.cacheAge.set(null);
  }
}
